/**
 * Service layer — ON DECK, the tower's to-do list (docs/OPERATING_MODEL.md build step 7).
 * Talks to /api/on-deck only. The kit items shown on it are ticked through services/gates.js
 * (setGateItemStatus), the same shared project facts every milestone reads.
 */

async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* not ready */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}
async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/on-deck', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/** { onDeck: [...], suggestions: [...], dry: { ready, seatsFreeing, idle, landingSeats, idleNames, dry } } */
export function getOnDeckBoard() { return post({ action: 'board' }) }
export function pullOnDeck(milestoneId, { memberId, memberName, note } = {}) { return post({ action: 'pull', milestoneId, memberId, memberName, note }) }
export function dropOnDeck(milestoneId, memberName) { return post({ action: 'drop', milestoneId, memberName }) }
/** The running-dry lockout, for the tower only: { gated, dry, onDeck, suggestions, snoozeInfo }. */
export function getOnDeckGate(memberId) { return post({ action: 'gate', memberId }) }
export function snoozeOnDeck(memberId) { return post({ action: 'snooze', memberId }) }
/** Chase a kit item someone else owes: record when it's expected and nudge the coordinator in the
 *  project's Slack channel. Returns the fresh board plus { chased: { ok, error } }. */
export function chaseKitItem(itemId, { expectedOn, note, memberName } = {}) { return post({ action: 'chase', itemId, expectedOn, note, memberName }) }

/* ── Is the line-up short? (Matt, 2026-09-27: the line-up is the tower's main tool, not a lockout) ──
   One shared reading for the Line-up tab's headline and the Flight Board menu count: READY lined-up
   milestones vs PEOPLE who will need new work this week. Cached for five minutes; anything that changes
   the line-up calls noteLineUpChanged() so every reader refreshes at once. */
let lineUpCache = null, lineUpAt = 0, lineUpInFlight = null
const lineUpSubs = new Set()
export function getLineUpShort({ force } = {}) {
  if (!force && lineUpCache && Date.now() - lineUpAt < 5 * 60 * 1000) return Promise.resolve(lineUpCache)
  if (lineUpInFlight) return lineUpInFlight
  lineUpInFlight = getOnDeckBoard().then(b => {
    const d = (b && b.dry) || {}
    lineUpCache = { short: !!d.dry, ready: d.ready || 0, needWork: d.needWork || 0, board: b }
    lineUpAt = Date.now()
    lineUpSubs.forEach(fn => { try { fn(lineUpCache) } catch (_) { /* a reader's problem */ } })
    return lineUpCache
  }).finally(() => { lineUpInFlight = null })
  return lineUpInFlight
}
export function onLineUpShort(fn) { lineUpSubs.add(fn); return () => lineUpSubs.delete(fn) }
export function noteLineUpChanged(board) {
  if (board && board.dry) {
    lineUpCache = { short: !!board.dry.dry, ready: board.dry.ready || 0, needWork: board.dry.needWork || 0, board }
    lineUpAt = Date.now()
    lineUpSubs.forEach(fn => { try { fn(lineUpCache) } catch (_) { /* a reader's problem */ } })
  } else { lineUpAt = 0; getLineUpShort({ force: true }).catch(() => {}) }
}
